// Shadow Network — Copy profile URL button.
// Tiny share affordance to boost organic virality on X/Telegram/etc.
// Uses window.location.origin so it copies the right host (preview vs prod)
// automatically. Falls back to legacy execCommand on browsers without the
// async Clipboard API.
import React, { useState } from "react";
import { Link as LinkIcon, Check } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const I18N = {
  copy: { es: "Copiar URL", en: "Copy URL", sk: "Kopírovať URL", de: "URL kopieren", "zh-CN": "复制链接" },
  copied: { es: "¡Copiado!", en: "Copied!", sk: "Skopírované!", de: "Kopiert!", "zh-CN": "已复制！" },
};

const PUBLIC_HOST = "https://rebelbtc.com";

const buildUrl = (username) => {
  if (!username) return null;
  // Always use the canonical production host for shareable URLs.
  // Copying preview URLs to X/Telegram would break for visitors.
  return `${PUBLIC_HOST}/shadow/perfil/${username}`;
};

const writeToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Legacy fallback (older Safari / WebViews)
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
};

export const CopyProfileUrlButton = ({ username, variant = "pill", className = "" }) => {
  const { lang } = useLanguage();
  const [copied, setCopied] = useState(false);
  const url = buildUrl(username);
  if (!url) return null;

  const onCopy = async () => {
    try {
      // MEJORA: try the Web Share API first (mobile native share sheet).
      // Falls back to clipboard on desktop.
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({
            title: `@${username} · RE₿EL Shadow Network`,
            text: "Mira este perfil en RE₿EL Academy",
            url,
          });
          // Native share completed — no need to show "copied" state.
          return;
        } catch (shareErr) {
          // User dismissed the share sheet, or share rejected — fall through
          // to clipboard so the URL is still copied for them.
          if (shareErr && shareErr.name === "AbortError") return;
        }
      }
      await writeToClipboard(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Silent — clipboard blocked. Could show a tooltip but we keep it tiny.
    }
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={onCopy}
        title={copied ? I18N.copied[lang] : I18N.copy[lang]}
        data-testid="copy-profile-url-btn"
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-white/70 transition-all hover:border-amber-400/60 hover:text-amber-300 ${className}`}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <LinkIcon className="h-3.5 w-3.5" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid="copy-profile-url-btn"
      className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
        copied
          ? "border-emerald-400/50 bg-emerald-400/[0.08] text-emerald-300"
          : "border-white/20 text-white hover:border-amber-400/60 hover:text-amber-300"
      } ${className}`}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}
      {copied ? I18N.copied[lang] : I18N.copy[lang]}
    </button>
  );
};

export default CopyProfileUrlButton;
